import * as React from "react"
import { cn } from "@/lib/utils"

// An admin collection (M7 decision 14): a card list under 640px (the design
// system's Listings pattern, one card per row) and a real <table> above it
// (the Data table section's advice for dense, multi-column data). Both are
// rendered and CSS picks one, so SSR and hydration always agree; images in
// the hidden half are lazy and never load.

export type ListColumn<T> = {
  key: string
  header: React.ReactNode
  cell: (item: T) => React.ReactNode
  /** Extra classes for the header and body cells (widths, alignment). */
  className?: string
  /** Visually hidden header (actions, images). */
  srOnlyHeader?: boolean
}

export function ResponsiveList<T>({
  items,
  getKey,
  columns,
  card,
  label,
  rowClassName,
}: {
  items: T[]
  getKey: (item: T) => React.Key
  columns: ListColumn<T>[]
  /** One item as a phone card. */
  card: (item: T) => React.ReactNode
  /** Names the list for assistive tech (and the table's caption). */
  label: string
  rowClassName?: (item: T) => string | undefined
}) {
  return (
    <>
      <ul
        aria-label={label}
        className="divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page sm:hidden"
      >
        {items.map((item) => (
          <li key={getKey(item)} className={rowClassName?.(item)}>
            {card(item)}
          </li>
        ))}
      </ul>

      {/* relative: the scroll box is also the containing block for the
          absolutely positioned sr-only text in the rows' buttons, which
          otherwise escaped it and widened the whole page (M7 W3, 768px). */}
      <div className="relative hidden overflow-x-auto rounded-md border border-hairline bg-page sm:block">
        <table className="w-full border-collapse text-left text-sm">
          <caption className="sr-only">{label}</caption>
          <thead className="border-b border-hairline bg-surface">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    "px-4 py-3 text-xs font-medium uppercase tracking-wider text-ink-muted",
                    column.className,
                  )}
                >
                  {column.srOnlyHeader ? <span className="sr-only">{column.header}</span> : column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {items.map((item) => (
              <tr key={getKey(item)} className={cn("align-middle", rowClassName?.(item))}>
                {columns.map((column) => (
                  <td key={column.key} className={cn("px-4 py-3 text-ink-body", column.className)}>
                    {column.cell(item)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

/** "Showing newest 500 of 812 enquiries." when the server capped the list (W1: `limit` 500). */
export function CapNote({ limit, total, noun }: { limit: number; total: number; noun: string }) {
  if (total <= limit) return null
  return (
    <p className="mt-3">
      Showing newest {limit.toLocaleString("en-GB")} of {total.toLocaleString("en-GB")} {noun}.
    </p>
  )
}
